import React, { useState, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Download, 
  Printer, 
  Check, 
  X,
  AlertCircle,
  ExternalLink,
  Key,
  Globe,
  BarChart,
  Search,
  Mail,
  Server
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface AccessItem {
  id: string;
  category: 'essential' | 'analytics' | 'seo' | 'hosting' | 'marketing';
  service: string;
  description: string;
  required: boolean;
  priority: 'high' | 'medium' | 'low';
  accessType: string;
  instructions: string;
  icon: any;
  status?: 'pending' | 'received' | 'configured';
  notes?: string;
}

export const AccessRequirementsForm: React.FC = () => {
  const documentRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [clientName, setClientName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');

  const handleDownloadPDF = async () => {
    if (!documentRef.current || isGenerating) return;
    
    setIsGenerating(true);
    
    try {
      const element = documentRef.current;
      
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
        hotfixes: ['px_scaling']
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 0;
      const totalPages = Math.ceil(imgHeight / pdfHeight);

      while (heightLeft > 0) {
        if (pageNumber > 0) {
          pdf.addPage();
        }
        
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0,
          position,
          imgWidth,
          imgHeight,
          undefined,
          'FAST'
        );
        
        // Ajouter numéro de page
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Page ${pageNumber + 1} / ${totalPages}`,
          pdfWidth / 2,
          pdfHeight - 20,
          { align: 'center' }
        );
        
        heightLeft -= pdfHeight;
        position -= pdfHeight;
        pageNumber++;
      }

      const fileName = clientName 
        ? `Besoins_Acces_STRATOS_${clientName.replace(/\s+/g, '_')}.pdf`
        : 'Besoins_Acces_STRATOS.pdf';
      
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setIsGenerating(false);
    }
  };

  const accessItems: AccessItem[] = [
    // ESSENTIAL
    {
      id: 'wordpress',
      category: 'essential',
      service: 'WordPress / CMS Admin',
      description: 'Accès administrateur au site web pour optimisations techniques',
      required: true,
      priority: 'high',
      accessType: 'Compte administrateur',
      instructions: 'Créer un compte admin avec email stratos@stratos360.fr ou nous communiquer identifiants temporaires',
      icon: Globe,
      status: 'pending'
    },
    {
      id: 'ftp',
      category: 'hosting',
      service: 'FTP / SFTP',
      description: 'Accès serveur pour interventions techniques avancées',
      required: false,
      priority: 'medium',
      accessType: 'Identifiants FTP',
      instructions: 'Fournir : Hôte, Port, Nom d\'utilisateur, Mot de passe',
      icon: Server,
      status: 'pending'
    },

    // ANALYTICS
    {
      id: 'ga4',
      category: 'analytics',
      service: 'Google Analytics 4',
      description: 'Suivi et analyse du trafic, comportement utilisateurs, conversions',
      required: true,
      priority: 'high',
      accessType: 'Accès éditeur ou administrateur',
      instructions: 'Ajouter l\'email stratos@stratos360.fr comme "Éditeur" dans Google Analytics',
      icon: BarChart,
      status: 'pending'
    },
    {
      id: 'gsc',
      category: 'seo',
      service: 'Google Search Console',
      description: 'Monitoring SEO, indexation, performances dans Google',
      required: true,
      priority: 'high',
      accessType: 'Propriétaire ou Utilisateur complet',
      instructions: 'Ajouter stratos@stratos360.fr dans Search Console > Paramètres > Utilisateurs et autorisations',
      icon: Search,
      status: 'pending'
    },
    {
      id: 'gtm',
      category: 'analytics',
      service: 'Google Tag Manager',
      description: 'Gestion des balises de tracking',
      required: false,
      priority: 'medium',
      accessType: 'Utilisateur avec droits de publication',
      instructions: 'Ajouter comme utilisateur avec droits de publication si GTM est utilisé',
      icon: BarChart,
      status: 'pending'
    },

    // SEO & LOCAL
    {
      id: 'gbp',
      category: 'seo',
      service: 'Google Business Profile (ex GMB)',
      description: 'Gestion et optimisation de la fiche locale Google',
      required: true,
      priority: 'high',
      accessType: 'Gestionnaire',
      instructions: 'Ajouter stratos@stratos360.fr comme "Gestionnaire" dans Google Business Profile',
      icon: Globe,
      status: 'pending'
    },
    {
      id: 'bing-webmaster',
      category: 'seo',
      service: 'Bing Webmaster Tools',
      description: 'SEO pour Bing/Microsoft',
      required: false,
      priority: 'low',
      accessType: 'Utilisateur',
      instructions: 'Ajouter comme utilisateur si le site est vérifié sur Bing',
      icon: Search,
      status: 'pending'
    },

    // SOCIAL & MARKETING
    {
      id: 'facebook',
      category: 'marketing',
      service: 'Facebook Business Manager',
      description: 'Gestion page Facebook et publicités',
      required: false,
      priority: 'medium',
      accessType: 'Admin ou Éditeur',
      instructions: 'Ajouter comme admin de la page Facebook professionnelle',
      icon: Mail,
      status: 'pending'
    },
    {
      id: 'instagram',
      category: 'marketing',
      service: 'Instagram Business',
      description: 'Gestion compte Instagram professionnel',
      required: false,
      priority: 'medium',
      accessType: 'Accès via Facebook Business',
      instructions: 'Lié au Facebook Business Manager',
      icon: Mail,
      status: 'pending'
    },

    // HOSTING
    {
      id: 'hosting-panel',
      category: 'hosting',
      service: 'Panneau hébergeur (cPanel/Plesk)',
      description: 'Gestion DNS, emails, bases de données',
      required: false,
      priority: 'low',
      accessType: 'Accès panneau',
      instructions: 'Uniquement si interventions techniques nécessaires',
      icon: Server,
      status: 'pending'
    },
    {
      id: 'domain',
      category: 'hosting',
      service: 'Gestion nom de domaine',
      description: 'Modification DNS si nécessaire',
      required: false,
      priority: 'low',
      accessType: 'Accès registrar',
      instructions: 'Pour modifications DNS avancées',
      icon: Globe,
      status: 'pending'
    },

    // TOOLS
    {
      id: 'email',
      category: 'essential',
      service: 'Email professionnel',
      description: 'Communication et notifications',
      required: true,
      priority: 'high',
      accessType: 'Email de contact',
      instructions: 'Fournir l\'email principal pour les communications et rapports',
      icon: Mail,
      status: 'pending'
    },
  ];

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      essential: 'Essentiel',
      analytics: 'Analytics',
      seo: 'SEO',
      hosting: 'Hébergement',
      marketing: 'Marketing'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      essential: 'bg-red-100 text-red-700',
      analytics: 'bg-blue-100 text-blue-700',
      seo: 'bg-green-100 text-green-700',
      hosting: 'bg-purple-100 text-purple-700',
      marketing: 'bg-yellow-100 text-yellow-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const requiredItems = accessItems.filter(item => item.required);
  const optionalItems = accessItems.filter(item => !item.required);

  return (
    <div className="space-y-6">
      {/* Form Header */}
      <Card className="p-6 print:hidden">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Formulaire de Besoins d'Accès
        </h2>
        <p className="text-gray-600 mb-6">
          Pour démarrer votre accompagnement SEO & GEO dans les meilleures conditions, 
          nous avons besoin des accès suivants.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <input
            type="text"
            placeholder="Nom de l'établissement *"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
          />
          <input
            type="email"
            placeholder="Email de contact *"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
          />
          <input
            type="url"
            placeholder="URL du site web *"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
          />
        </div>

        <div className="flex gap-3">
          <Button onClick={() => window.print()}>
            <Printer className="w-4 h-4 mr-2" />
            Imprimer
          </Button>
          <Button variant="outline" onClick={handleDownloadPDF} disabled={isGenerating}>
            <Download className="w-4 h-4 mr-2" />
            {isGenerating ? 'Génération...' : 'Télécharger PDF'}
          </Button>
          <Button variant="outline">
            <Mail className="w-4 h-4 mr-2" />
            Envoyer au client
          </Button>
        </div>
        {isGenerating && (
          <p className="text-sm text-blue-600 mt-3 animate-pulse">
            ⏳ Génération du PDF en cours... Veuillez patienter.
          </p>
        )}
      </Card>

      {/* Document */}
      <div ref={documentRef}>
      <Card className="p-12 bg-white print:shadow-none print:border-0">
        {/* Header */}
        <div className="flex items-start justify-between mb-12 pb-8 border-b-2 border-stratos-gold">
          <div>
            <h1 
              className="text-5xl font-bold mb-2"
              style={{
                background: 'linear-gradient(to right, #E9BC66, #F4D89E)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              STRATOS
            </h1>
            <p className="text-xl text-gray-600 font-light">Hospitality</p>
            <p className="text-sm text-gray-500 mt-2">by Getup Agency</p>
          </div>
          <div className="text-right">
            <Badge variant="default" className="text-lg px-4 py-2 mb-4">
              <Key className="w-4 h-4 mr-2 inline" />
              BESOINS D'ACCÈS
            </Badge>
            <p className="text-sm text-gray-600">
              Date : {new Date().toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>

        {/* Client Info */}
        <div className="mb-8 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-bold text-stratos-gold uppercase mb-3">
            Informations Client
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Établissement</p>
              <p className="font-semibold text-gray-900">
                {clientName || '[Nom de l\'établissement]'}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Contact</p>
              <p className="font-semibold text-gray-900">
                {contactEmail || '[Email de contact]'}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Site web</p>
              <p className="font-semibold text-gray-900">
                {websiteUrl || '[URL du site]'}
              </p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="mb-8 text-sm text-gray-700 leading-relaxed">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Liste des Accès Nécessaires
          </h2>
          <p className="mb-3">
            Cher Client,
          </p>
          <p className="mb-3">
            Afin de démarrer votre accompagnement SEO & GEO dans les meilleures conditions et d'optimiser 
            votre visibilité en ligne, nous avons besoin d'accéder à différents outils et plateformes.
          </p>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">🔒 Sécurité & Confidentialité garanties</p>
              <p>
                Tous les accès sont traités de manière confidentielle et sécurisée. Nous respectons le RGPD 
                et nos engagements de confidentialité. Les accès seront utilisés uniquement dans le cadre 
                de votre prestation SEO/GEO.
              </p>
            </div>
          </div>
        </div>

        {/* Required Access */}
        <div className="mb-8 print:break-inside-avoid">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">
              Accès Obligatoires
            </h3>
            <Badge variant="danger">Requis pour démarrer</Badge>
          </div>

          <div className="space-y-4">
            {requiredItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  className="p-5 border-2 border-red-200 rounded-lg bg-red-50/50 hover:shadow-md transition-shadow print:break-inside-avoid"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-base font-bold text-gray-900 mb-1">
                            {index + 1}. {item.service}
                          </h4>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="danger">Obligatoire</Badge>
                            <Badge variant="outline" className={getCategoryColor(item.category)}>
                              {getCategoryLabel(item.category)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">
                        {item.description}
                      </p>
                      <div className="p-3 bg-white rounded border border-gray-200">
                        <p className="text-xs font-semibold text-gray-900 uppercase mb-1">
                          Type d'accès requis
                        </p>
                        <p className="text-sm text-gray-700 mb-2">
                          {item.accessType}
                        </p>
                        <p className="text-xs font-semibold text-gray-900 uppercase mb-1">
                          Instructions
                        </p>
                        <p className="text-sm text-gray-700">
                          {item.instructions}
                        </p>
                      </div>

                      {/* Checkbox pour le suivi */}
                      <div className="mt-3 flex items-center gap-6 text-sm">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" className="w-4 h-4 text-stratos-gold rounded" />
                          <span className="text-gray-600">Accès fourni</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" className="w-4 h-4 text-stratos-gold rounded" />
                          <span className="text-gray-600">Vérifié & configuré</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Optional Access */}
        <div className="mb-8 print:break-inside-avoid">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <Check className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">
              Accès Recommandés (Optionnels)
            </h3>
            <Badge variant="default">Pour optimiser les résultats</Badge>
          </div>

          <div className="space-y-3">
            {optionalItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  className="p-4 border border-gray-200 rounded-lg hover:border-stratos-gold hover:shadow-sm transition-all print:break-inside-avoid"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-base font-semibold text-gray-900 mb-1">
                            {item.service}
                          </h4>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={item.priority === 'medium' ? 'warning' : 'outline'}
                            >
                              {item.priority === 'medium' ? 'Recommandé' : 'Optionnel'}
                            </Badge>
                            <Badge variant="outline" className={getCategoryColor(item.category)}>
                              {getCategoryLabel(item.category)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {item.description}
                      </p>
                      <p className="text-xs text-gray-500 italic">
                        {item.instructions}
                      </p>
                      
                      <div className="mt-2 flex items-center gap-4 text-sm">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" className="w-4 h-4 text-stratos-gold rounded" />
                          <span className="text-gray-600">Accès fourni</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Important Notes */}
        <div className="mb-8 p-6 bg-yellow-50 border-2 border-yellow-200 rounded-lg print:break-inside-avoid">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            Notes Importantes
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Création de comptes :</strong> Pour certains services, nous recommandons de créer 
                un compte dédié (ex: stratos@votredomaine.com) plutôt que de partager vos identifiants personnels.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Transmission sécurisée :</strong> Envoyez les accès via un canal sécurisé 
                (ex: email chiffré, outil de partage sécurisé). Ne jamais envoyer login et mot de passe 
                dans le même email.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Niveau d'accès :</strong> Fournissez les niveaux d'accès recommandés (Admin, Éditeur, etc.) 
                pour permettre toutes les optimisations nécessaires.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Délai :</strong> Merci de fournir ces accès dans les <strong>7 jours suivant 
                la signature du contrat</strong> pour permettre un démarrage optimal de la prestation.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Support :</strong> En cas de difficulté pour fournir un accès, contactez-nous 
                immédiatement. Nous vous accompagnerons dans la procédure.
              </span>
            </li>
          </ul>
        </div>

        {/* Contact Section */}
        <div className="mb-8 p-6 bg-gray-50 rounded-lg print:break-inside-avoid">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Comment nous transmettre les accès ?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">📧 Par email (sécurisé)</h4>
              <p className="text-gray-700 mb-1">
                <strong>Adrien Cerdan</strong> – Directeur
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">💬 Support technique</h4>
              <p className="text-gray-700 mb-2">
                Email : support@stratos-hospitality.com
              </p>
              <p className="text-gray-700">
                Plateforme STRATOS (après activation)
              </p>
            </div>
          </div>
        </div>

        {/* Validation Section */}
        <div className="pt-8 border-t-2 border-stratos-gold print:break-inside-avoid">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Confirmation de réception
          </h3>
          <div className="grid grid-cols-2 gap-8 text-sm">
            <div>
              <p className="text-gray-600 mb-2">Je confirme avoir pris connaissance des accès requis :</p>
              <div className="pt-16 border-t border-gray-300">
                <p className="text-xs text-gray-500">Date et signature du client</p>
              </div>
            </div>
            <div>
              <p className="text-gray-600 mb-2">Accès reçus et vérifiés par STRATOS :</p>
              <div className="pt-16 border-t border-gray-300">
                <p className="text-xs text-gray-500">Date et signature STRATOS</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
            <div className="mt-12 pt-6 border-t text-center text-xs text-gray-500">
              <p className="mb-2">
                <strong>STRATOS Hospitality</strong> (by Getup Agency) – SARL au capital de 150 500€
              </p>
              <p>10 impasse de l'Olivaie, 83210 La Farlède</p>
              <p>SIRET : 822 096 913 00022 – TVA : FR82822096913</p>
              <p className="mt-3">
                Représentée par Adrien Cerdan
              </p>
              <p className="mt-2 text-gray-400">
                Document confidentiel – Usage réservé au client et STRATOS uniquement
              </p>
            </div>
      </Card>
      </div>
    </div>
  );
};

